// Google Sheets API utility functions
import { SHEETS_CONFIG, buildUrl } from '../config/sheets-config';

// Generic function to make API calls to Google Apps Script
const callGoogleSheets = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...options.headers
      },
      body: options.body,
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Google Sheets API error:', error);
    throw error;
  }
};

// Convert form data to URL-encoded string
const encodeFormData = (data) => {
  const formData = new URLSearchParams();
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      formData.append(key, data[key]);
    }
  });
  return formData.toString();
};

// Get all events
export const getEvents = async () => {
  try {
    const url = buildUrl(SHEETS_CONFIG.ENDPOINTS.EVENTS);
    const response = await callGoogleSheets(url);
    
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || 'Failed to fetch events');
    }
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

// Get time slots for a specific event
export const getTimeSlots = async (eventId) => {
  try {
    console.log('getTimeSlots called with eventId:', eventId);
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const url = buildUrl(`${SHEETS_CONFIG.ENDPOINTS.TIME_SLOTS}&eventId=${eventId}&t=${timestamp}`);
    console.log('Fetching from URL:', url);
    
    const response = await callGoogleSheets(url);
    console.log('Raw response from Google Sheets:', response);
    console.log('Response type:', typeof response);
    console.log('Is array?', Array.isArray(response));
    
    // The Google Apps Script returns the time slots directly as an array
    // Check if response is an array (success) or has error properties
    if (Array.isArray(response)) {
      console.log('Response is array, returning:', response);
      return response;
    } else if (response.success) {
      console.log('Response has success property, returning data:', response.data);
      return response.data;
    } else {
      console.log('Response has error, throwing:', response.error);
      throw new Error(response.error || 'Failed to fetch time slots');
    }
  } catch (error) {
    console.error('Error in getTimeSlots:', error);
    throw error;
  }
};

// Get all registrations
export const getRegistrations = async () => {
  try {
    const url = buildUrl(SHEETS_CONFIG.ENDPOINTS.REGISTRATIONS);
    const response = await callGoogleSheets(url);
    
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || 'Failed to fetch registrations');
    }
  } catch (error) {
    console.error('Error fetching registrations:', error);
    throw error;
  }
};

// Get all feedback
export const getFeedback = async () => {
  try {
    const url = buildUrl(SHEETS_CONFIG.ENDPOINTS.FEEDBACK);
    const response = await callGoogleSheets(url);
    
    // The Google Apps Script returns the feedback directly as an array
    // Check if response is an array (success) or has error properties
    if (Array.isArray(response)) {
      return response;
    } else if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || 'Failed to fetch feedback');
    }
  } catch (error) {
    console.error('Error fetching feedback:', error);
    throw error;
  }
};

// Submit a new registration
export const submitRegistration = async (registrationData) => {
  try {
    const url = SHEETS_CONFIG.SCRIPT_URL;
    const formData = {
      action: 'register',
      ...registrationData
    };
    
    const response = await callGoogleSheets(url, {
      method: 'POST',
      body: encodeFormData(formData)
    });
    
    if (response.success) {
      return response;
    } else {
      throw new Error(response.error || 'Failed to submit registration');
    }
  } catch (error) {
    console.error('Error submitting registration:', error);
    throw error;
  }
};

// Submit feedback
export const submitFeedback = async (feedbackData) => {
  try {
    const url = SHEETS_CONFIG.SCRIPT_URL;
    
         // Map the field names to match what Google Apps Script expects
     const mappedData = {
       action: 'feedback',
       name: feedbackData.parent_name || 'Anonymous', // Google Apps Script expects 'name'
       email: feedbackData.email || 'Not provided', // Google Apps Script expects 'email'
       message: feedbackData.feedback_message, // Google Apps Script expects 'message'
       rating: feedbackData.rating, // Optional field
       eventAttended: feedbackData.event_attended, // Additional field for your records
       childName: feedbackData.child_name || 'Not provided' // Additional field for your records
     };
    
    console.log('submitFeedback sending data:', mappedData);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeFormData(mappedData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

         const result = await response.json();
     console.log('submitFeedback response:', result);
     console.log('Response status:', response.status);
     console.log('Response headers:', response.headers);
     
     if (result.success) {
       return result;
     } else {
       console.error('Google Apps Script error response:', result);
       throw new Error(result.error || 'Failed to submit feedback');
     }
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

// Check existing registrations for a user
export const checkExistingRegistrations = async (email, phone, eventId) => {
  try {
    const registrations = await getRegistrations();
    
    if (!registrations || !Array.isArray(registrations)) {
      return 0;
    }
    
    const existingCount = registrations.filter(reg => {
      return String(reg.event_id) === String(eventId) &&
             (reg.email === email || reg.phone === phone);
    }).length;
    
    return existingCount;
  } catch (error) {
    console.error('Error checking existing registrations:', error);
    return 0;
  }
};

// Refresh time slots data (useful after registration)
export const refreshTimeSlots = async (eventId) => {
  try {
    return await getTimeSlots(eventId);
  } catch (error) {
    console.error('Error refreshing time slots:', error);
    throw error;
  }
};

// Get event details by ID
export const getEventById = async (eventId) => {
  try {
    const events = await getEvents();
    const event = events.find(e => String(e.event_id) === String(eventId));
    return event || null;
  } catch (error) {
    console.error('Error getting event by ID:', error);
    return null;
  }
};

// Parse integer sheet cell; preserve 0 (do not use || which treats 0 as missing).
const parseSlotInt = (value, whenMissing) => {
  if (value === '' || value === undefined || value === null) return whenMissing;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : whenMissing;
};

// Match sheet rows to canonical UI labels when wording differs (e.g. "1:00 PM" vs "1:00 - 1:30").
const slotStartKey = (label) => {
  const m = String(label || '').match(/(\d{1,2})\s*:\s*(\d{2})/);
  if (!m) return String(label || '').trim().toLowerCase();
  return `${parseInt(m[1], 10)}:${m[2]}`;
};

export const slotTimesMatch = (a, b) => {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  if (left === right) return true;
  return slotStartKey(left) === slotStartKey(right);
};

/** Resolve selected UI slot to merged sheet-backed availability (same logic as the picker). */
export const findMergedSlotBySelection = (
  canonicalSlots,
  apiSlotsRaw,
  selectedSlot
) => {
  const merged = mergeCanonicalTimeSlots(canonicalSlots, apiSlotsRaw);
  return merged.find(
    (slot) =>
      slot.id === selectedSlot.id ||
      slot.time === selectedSlot.time ||
      slotTimesMatch(slot.time, selectedSlot.time)
  );
};

/**
 * Always returns one entry per canonical slot. Uses sheet counts when a row matches.
 * Rows with matchSheetLoose: false must match the sheet "Time" cell exactly (e.g. June 14 / July 19 slots).
 * @param {Array<{id?:string,time:string,day?:string,max_spots?:number,available_spots?:number,status?:string,matchSheetLoose?:boolean}>} canonicalSlots
 * @param {Array<Record<string, unknown>>} apiSlotsRaw rows from Google Apps Script
 */
export const mergeCanonicalTimeSlots = (canonicalSlots, apiSlotsRaw) => {
  const list = Array.isArray(apiSlotsRaw) ? apiSlotsRaw : [];
  if (list.length === 0) {
    return canonicalSlots.map((s) => ({ ...s }));
  }
  const used = new Set();

  const takeExact = (timeLabel) => {
    const t = String(timeLabel || '').trim();
    const idx = list.findIndex(
      (row, i) =>
        !used.has(i) && String(row.time || row.id || '').trim() === t
    );
    if (idx === -1) return null;
    used.add(idx);
    return list[idx];
  };

  const takeByStartKey = (timeLabel) => {
    const key = slotStartKey(timeLabel);
    const idx = list.findIndex(
      (row, i) =>
        !used.has(i) && slotStartKey(row.time || row.id || '') === key
    );
    if (idx === -1) return null;
    used.add(idx);
    return list[idx];
  };

  return canonicalSlots.map((def) => {
    const allowLoose = def.matchSheetLoose !== false;
    const matchRaw = takeExact(def.time) || (allowLoose ? takeByStartKey(def.time) : null);
    if (!matchRaw) {
      return { ...def };
    }
    const f = formatTimeSlot(matchRaw);
    return {
      ...def,
      id: def.time,
      time: def.time,
      max_spots: f.max_spots,
      available_spots: f.available_spots,
      status: f.status,
      day: f.day && String(f.day).trim() ? f.day : def.day
    };
  });
};

// Utility function to format time slot data for display
export const formatTimeSlot = (timeSlot) => {
  const max_spots = parseSlotInt(timeSlot.max_spots, 2);
  const available_spots = parseSlotInt(
    timeSlot.available_spots,
    max_spots
  );
  return {
    id: timeSlot.time,
    time: timeSlot.time,
    day: timeSlot.day,
    max_spots,
    available_spots,
    status: timeSlot.status || 'Available'
  };
};

// Utility function to check if a time slot is available
export const isTimeSlotAvailable = (timeSlot) => {
  // Check both available_spots and status to handle data inconsistencies
  // A time slot is available if:
  // 1. It has at least 1 spot available AND
  // 2. Status is not "Full"
  const hasSpots = parseInt(timeSlot.available_spots) > 0;

  // If status says "Full", respect that regardless of available_spots
  if (timeSlot.status === 'Full') {
    return false;
  }
  
  // Otherwise, check available spots
  return hasSpots;
};

// Utility function to get available time slots
export const getAvailableTimeSlots = (timeSlots) => {
  return timeSlots.filter(isTimeSlotAvailable);
};
